// Картата „Изписване от склад" при разнасяне: заглавие с броя позиции,
// заглавен ред, редовете на продуктите (ReportPostingWriteoffRows), подредовете
// на избраните заместители и ред „Общо изписване". Изписването е ВИНАГИ FIFO.
// Плътна, с височината на справките (`postingDensity.ts`): нисък заглавен ред,
// редове 34px с мишка / 44px с пръст, решетката се побира без хоризонтален
// скрол от ~744px нагоре (по-тясно — скролира картата, не страницата).
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ReportPostingPreview } from "../../../../api/inventory";
import { formatMoney } from "../../components/numbers";
import { CARD_HEAD, ROW_HEADER, ROW_HEADER_TEXT, ROW_TOTAL } from "../../components/list/density";
import { WRITEOFF_GRID_CLASS, WRITEOFF_GRID_MIN_CLASS } from "./reportPostingShared";
import { DonorRow, ProductWriteoffRows } from "./ReportPostingWriteoffRows";
import type { ReportPostingWorksheetState } from "./ReportPostingWorksheetState";

// antd вътрешности на плоския селект за склад и полето за количество в ниските
// редове (size="small", 24px) — недостижими за Tailwind. Селектът „multiple"
// по подразбиране е 27px (отстъп 1px + полето за търсене с margin 2px) и
// вдигаше реда на 36px — тук е изравнен на 24px (и полето за количество — от
// 24,5 на 24px), за да е редът 34px като в справките. С пръст (pointer: coarse
// — и таблет в landscape) контролите растат до 36px, редът — до 44px.
// `pst-wh-missing` — продукт без склад: подканата „Изберете" е кехлибарена
// (липсва настройка, не грешка).
const WRITEOFF_TABLE_CSS = `
  .pst-rows .pst-wh .ant-select-selector {
    padding-inline: 0 !important; padding-block: 0 !important;
    min-height: 24px !important; align-items: center !important;
  }
  .pst-rows .pst-wh .ant-select-selection-overflow-item-suffix { margin-block: 0 !important; }
  .pst-rows .pst-wh .ant-select-selection-item {
    background: transparent !important; border: none !important;
    height: 20px; line-height: 18px !important; margin: 0 6px 0 0 !important;
    font-size: 13px; color: var(--color-ink-2); padding-inline: 0 !important;
  }
  .pst-rows .pst-wh .ant-select-selection-placeholder { font-size: 13px; }
  .pst-rows .pst-wh-missing .ant-select-selection-placeholder { color: var(--color-brand-deep-ink); }
  .pst-rows .pst-wh:hover .ant-select-selector { background: var(--color-shade-1) !important; border-radius: 6px; }
  /* vertical-align: без него полето стои на базовата линия и е 24,5px */
  .pst-rows .ant-input-number-input { text-align: right; font-size: 13px; vertical-align: top; }
  @media (pointer: coarse) {
    .pst-rows .pst-wh .ant-select-selector { min-height: 36px !important; }
    .pst-rows .ant-input-number-sm .ant-input-number-input { height: 34px !important; }
  }
`;

type Props = {
  products: ReportPostingPreview["products"];
  state: ReportPostingWorksheetState;
  grossFactorOf: (productId: number) => number;
  decimalsOf: (productId: number) => number;
  // Слотът до склада на всеки продукт (белег/действие за склада му) — по
  // избор; без него редът е както досега
  renderWarehouseAddon?: (product: ReportPostingPreview["products"][number]) => ReactNode;
};

export const ReportPostingWriteoffCard = ({
  products,
  state,
  grossFactorOf,
  decimalsOf,
  renderWarehouseAddon,
}: Props) => {
  const { t } = useTranslation();
  const headers: { label: string; right?: boolean }[] = [
    { label: t("inventoryPage.colProductName") },
    { label: t("inventoryPage.postingColWarehouse") },
    { label: t("inventoryPage.postingColToWriteoff"), right: true },
    { label: t("inventoryPage.postingColAvail"), right: true },
    { label: t("inventoryPage.postingColAfter"), right: true },
    { label: t("inventoryPage.colNet"), right: true },
    { label: t("inventoryPage.colGross"), right: true },
  ];
  const warehouseNameOf = (productId: number, warehouseId: number) =>
    state.warehouseOptionsOf.get(productId)?.find((o) => o.value === warehouseId)?.label ??
    `#${warehouseId}`;
  const donorRowsCount = state.substitutions.length;

  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-shade-2 bg-panel">
      <div className={`flex items-baseline gap-2 ${CARD_HEAD}`}>
        <span className="text-[15px] font-semibold leading-[normal] text-ink">
          {t("inventoryPage.postingWriteoffTitle")}
        </span>
        <span className="text-[12.5px] leading-[normal] text-muted">
          {t("inventoryPage.postingPositions", { count: products.length + donorRowsCount })}
        </span>
      </div>
      <div className="pst-rows overflow-x-auto">
        <style>{WRITEOFF_TABLE_CSS}</style>
        <div className={WRITEOFF_GRID_MIN_CLASS}>
          <div className={`${WRITEOFF_GRID_CLASS} ${ROW_HEADER} border-y border-shade-2 bg-fill`}>
            {headers.map((header) => (
              <div
                key={header.label}
                className={`truncate ${ROW_HEADER_TEXT} ${header.right ? "text-right" : ""}`}
              >
                {header.label}
              </div>
            ))}
          </div>
          {products.map((product) => (
            <div key={product.productId}>
              <ProductWriteoffRows
                product={product}
                state={state}
                grossFactor={grossFactorOf(product.productId)}
                decimals={decimalsOf(product.productId)}
                warehouseAddon={renderWarehouseAddon?.(product)}
              />
              {/* Избраните заместители на продукта — точно под него */}
              {state.substitutions
                .filter((sub) => sub.recipientProductId === product.productId)
                .map((sub) => {
                  const donor = state.nameById.get(sub.donorProductId);
                  return (
                    <DonorRow
                      key={`${sub.warehouseId}-${sub.donorProductId}`}
                      name={donor?.name ?? `#${sub.donorProductId}`}
                      recipientName={product.productName}
                      warehouseName={warehouseNameOf(product.productId, sub.warehouseId)}
                      quantity={state.donorQuantityOf(sub) ?? sub.quantity}
                      stock={state.donorStockOf(sub.warehouseId, sub.donorProductId)}
                      unit={donor?.unit ?? null}
                      decimals={decimalsOf(sub.donorProductId)}
                    />
                  );
                })}
            </div>
          ))}
          {products.length === 0 && (
            <div className="px-3.5 py-2.5 text-[13px] leading-[normal] text-muted">
              {t("inventoryPage.noLines")}
            </div>
          )}
          {products.length > 0 && (
            <div className={`${WRITEOFF_GRID_CLASS} ${ROW_TOTAL} border-t border-shade-2 bg-fill`}>
              <div className={`col-span-5 ${ROW_HEADER_TEXT}`}>
                {t("inventoryPage.postingTotalWriteoff")}
              </div>
              <div className="text-right font-sheet text-[13px] font-semibold text-ink tabular-nums">
                {formatMoney(state.totalNet)}
              </div>
              <div className="text-right font-sheet text-[13px] font-bold text-brand-deep-ink tabular-nums">
                {formatMoney(state.totalGross)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportPostingWriteoffCard;
